import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Command } from 'cmdk';
import {
  Activity,
  ClipboardList,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Moon,
  ReceiptText,
  Search,
  Settings,
  Sun,
  SunMoon,
  Users,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/lib/auth/useAuth';
import { RoleRank, type Role } from '@/lib/auth/types';

interface Item {
  id: string;
  label: string;
  group: 'Navigate' | 'Theme' | 'Session';
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  minRole?: Role;
  run: () => void | Promise<void>;
}

/**
 * Cmd+K / Ctrl+K command palette. Mounts once at the layout level; toggles on
 * the global keyboard shortcut and offers role-filtered navigation + quick
 * theme switching + sign-out.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { setTheme } = useTheme();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  const items: Item[] = [
    { id: 'nav-dashboard',    label: 'Dashboard',              group: 'Navigate', icon: LayoutDashboard, minRole: 'Viewer',     run: () => { navigate('/dashboard'); close(); } },
    { id: 'nav-clients',      label: 'Clients',                group: 'Navigate', icon: Users,           minRole: 'Viewer',     run: () => { navigate('/clients'); close(); } },
    { id: 'nav-transactions', label: 'Transactions',           group: 'Navigate', icon: ReceiptText,     minRole: 'Viewer',     run: () => { navigate('/transactions'); close(); } },
    { id: 'nav-audit',        label: 'Audit trail',            group: 'Navigate', icon: ClipboardList,   minRole: 'Viewer',     run: () => { navigate('/audit'); close(); } },
    { id: 'nav-logs',         label: 'Logs',                   group: 'Navigate', icon: Activity,        minRole: 'Admin',      run: () => { navigate('/logs'); close(); } },
    { id: 'nav-api-keys',     label: 'API keys',               group: 'Navigate', icon: KeyRound,        minRole: 'Admin',      run: () => { navigate('/api-keys'); close(); } },
    { id: 'nav-users',        label: 'Admin users',            group: 'Navigate', icon: Users,           minRole: 'Admin',      run: () => { navigate('/users'); close(); } },
    { id: 'nav-settings',     label: 'Settings',               group: 'Navigate', icon: Settings,        minRole: 'SuperAdmin', run: () => { navigate('/settings'); close(); } },
    { id: 'theme-light',      label: 'Light theme',            group: 'Theme',    icon: Sun,                                      run: () => { setTheme('light');  close(); } },
    { id: 'theme-dark',       label: 'Dark theme',             group: 'Theme',    icon: Moon,                                     run: () => { setTheme('dark');   close(); } },
    { id: 'theme-system',     label: 'Follow system theme',    group: 'Theme',    icon: SunMoon,                                  run: () => { setTheme('system'); close(); } },
    { id: 'logout',           label: 'Sign out',               group: 'Session',  icon: LogOut,                                   run: async () => { await logout(); close(); navigate('/login', { replace: true }); } },
  ];

  const visible = items.filter((i) => !i.minRole || (user && RoleRank[user.role] >= RoleRank[i.minRole]));
  const groups: Item['group'][] = ['Navigate', 'Theme', 'Session'];

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-[20%] z-50 w-full max-w-xl -translate-x-1/2 rounded-lg border border-border bg-background shadow-xl"
          aria-describedby={undefined}
        >
          <DialogPrimitive.Title className="sr-only">Command palette</DialogPrimitive.Title>
          <Command className="flex flex-col">
            <div className="flex items-center gap-2 border-b border-border px-4 py-3">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Command.Input
                placeholder="Type a command or search…"
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                autoFocus
              />
              <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">ESC</kbd>
            </div>
            <Command.List className="max-h-80 overflow-y-auto p-2 text-sm">
              <Command.Empty className="p-6 text-center text-xs text-muted-foreground">
                No matching commands.
              </Command.Empty>
              {groups.map((group) => {
                const inGroup = visible.filter((i) => i.group === group);
                if (inGroup.length === 0) return null;
                return (
                  <Command.Group
                    key={group}
                    heading={group}
                    className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1"
                  >
                    {inGroup.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Command.Item
                          key={item.id}
                          value={item.label}
                          onSelect={() => void item.run()}
                          className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground"
                        >
                          <Icon className="h-4 w-4 text-muted-foreground" />
                          <span>{item.label}</span>
                        </Command.Item>
                      );
                    })}
                  </Command.Group>
                );
              })}
            </Command.List>
            <div className="flex items-center justify-between border-t border-border px-3 py-2 text-[10px] text-muted-foreground">
              <span>
                Tip: press <kbd className="rounded border border-border bg-muted px-1">⌘</kbd>
                <kbd className="rounded border border-border bg-muted px-1">K</kbd> anywhere.
              </span>
              <span>{visible.length} commands</span>
            </div>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
